/**
 * Aba "Histórico" da ficha da cliente, idêntica à do legado (`admin.html:2816-2820`, `5651-5741`;
 * linha 221 do inventário).
 *
 * Junta, do mais recente para o mais antigo: a criação do lead, a jornada, os questionários e
 * checklists, as mensagens e, com lead aberto, o funil, os compromissos, as parcelas pagas e os
 * contratos. A leitura mora em `ficha-timeline.ts` do pacote, e a montagem das frases em
 * `timeline/eventos.ts`.
 *
 * Território da frente A3. A moldura importa esta aba pelo nome `AbaTimeline`.
 */

// Estado e memória de função do React.
import { useCallback, useState } from 'react';
// As leituras do histórico.
import { lerLeiturasDaFicha, lerLeiturasDoLead, lerParcelasPagas } from '@studiolu/dados/areas/ficha-timeline';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// O contrato da aba.
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
// A montagem pura dos eventos e a data de cada um.
import { dataDoEvento, montarEventos, type EventoDoHistorico } from './timeline/eventos.js';
// As peças da A3.
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';

export function AbaTimeline({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="Histórico" />;
  return <ConteudoTimeline ficha={ficha} />;
}

/**
 * A origem do lead, quando a moldura a trouxer. Ela é opcional no contrato `LeadAberto` porque a
 * ausência dela é informação: no legado o lead vem inteiro pelo Kanban (`select('*')`,
 * `admin.html:6511`) e pelo compromisso (`4540`), e vem com quatro colunas pela lista de clientes
 * (`5878`), onde o Histórico escreve "Lead criada" sem origem.
 */
function origemDoLead(lead: FichaAberta['lead']): string | null {
  return typeof lead?.origem === 'string' ? lead.origem : null;
}

function ConteudoTimeline({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const escopo = useEscopoEstavel(ficha);
  // Os três valores do lead que a montagem usa, primitivos para o efeito não disparar à toa.
  const leadId = ficha.lead?.id ?? null;
  const leadCriadoEm = ficha.lead?.data_criacao ?? null;
  const leadOrigem = origemDoLead(ficha.lead);
  // `null` enquanto carrega: "Carregando…" (`admin.html:5658`).
  const [eventos, setEventos] = useState<readonly EventoDoHistorico[] | null>(null);

  // A leitura da aba, na sequência do legado: as seis da ficha em paralelo, depois as três do lead
  // em paralelo, depois as parcelas dos pagamentos encontrados. Pelo contrato de carga, a moldura
  // a roda ANTES de mostrar a aba, e por isso o "Carregando…" do legado só aparece a quem toca de
  // novo no botão da aba já aberta (`admin.html:5086` e `5658`).
  const carregar = useCallback(async () => {
    setEventos(null);
    const daFicha = await lerLeiturasDaFicha(dados, escopo);
    // Leitura que falha só deixa de somar eventos, como o `data || []` do legado.
    let statusHistorico: Parameters<typeof montarEventos>[0]['statusHistorico'] = [];
    let compromissos: Parameters<typeof montarEventos>[0]['compromissos'] = [];
    let pagamentos: Parameters<typeof montarEventos>[0]['pagamentos'] = [];
    let parcelas: Parameters<typeof montarEventos>[0]['parcelas'] = [];
    if (leadId) {
      const doLead = await lerLeiturasDoLead(dados, leadId);
      statusHistorico = (doLead.statusHistorico.ok && doLead.statusHistorico.dados) || [];
      compromissos = (doLead.compromissos.ok && doLead.compromissos.dados) || [];
      pagamentos = (doLead.pagamentos.ok && doLead.pagamentos.dados) || [];
      // Parcela não tem `lead_id`: só se consulta com pelo menos um pagamento (`5716-5717`).
      const pagIds = pagamentos.map((p) => p.id);
      if (pagIds.length > 0) {
        const lidas = await lerParcelasPagas(dados, pagIds);
        parcelas = (lidas.ok && lidas.dados) || [];
      }
    }
    setEventos(
      montarEventos({
        lead: leadId ? { id: leadId, data_criacao: leadCriadoEm, origem: leadOrigem } : null,
        historico: (daFicha.historico.ok && daFicha.historico.dados) || [],
        geral: daFicha.geral.ok ? daFicha.geral.dados : null,
        avaliativo: daFicha.avaliativo.ok ? daFicha.avaliativo.dados : null,
        atendimento: daFicha.atendimento.ok ? daFicha.atendimento.dados : null,
        milhoes: daFicha.milhoes.ok ? daFicha.milhoes.dados : null,
        mensagens: (daFicha.mensagens.ok && daFicha.mensagens.dados) || [],
        statusHistorico,
        compromissos,
        parcelas,
        pagamentos,
      }),
    );
  }, [dados, escopo, leadId, leadCriadoEm, leadOrigem]);
  useCarregadorDaAba(carregar);

  return (
    <section className="flex flex-col gap-cx-3">
      <h3 className="text-cx-base font-semibold text-texto">Histórico</h3>
      <p className="text-cx-sm text-texto-suave">
        Linha do tempo deste atendimento: junta o que já existe em cada aba, mais as mudanças de fase da jornada.
      </p>
      <div data-testid="timeline-cliente" className="flex flex-col">
        {eventos === null && <p className="text-cx-sm text-texto-suave">Carregando…</p>}
        {eventos !== null && eventos.length === 0 && (
          <p className="text-cx-sm text-texto-suave">Nenhum evento registrado ainda.</p>
        )}
        {eventos?.map((evento, indice) => (
          // A linha vertical liga um evento ao seguinte; no último ela some, como no legado (`841`).
          <div
            key={indice}
            data-testid="timeline-item"
            className={`relative border-l pb-cx-5 pl-cx-5 last:pb-0 ${
              indice === eventos.length - 1 ? 'border-transparent' : 'border-borda'
            }`}
          >
            {/* O ponto do evento, na cor de ação, sobre a linha. */}
            <span aria-hidden="true" className="absolute -left-[5px] top-[3px] h-[9px] w-[9px] rounded-cx-full bg-marca" />
            <div className="text-cx-xs uppercase tracking-wide text-texto-suave">{dataDoEvento(evento.data)}</div>
            <div className="text-cx-sm text-texto">{evento.texto}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
